import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';
import { trackPurchase } from '../utils/analytics';
import { useCart } from '../context/CartContext';

function OrderSuccessPage() {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const paymentMethod = searchParams.get('method') || 'online';
  const orderTotal = searchParams.get('total') || '0';
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Track Facebook Pixel Purchase event
    if (window.fbq && orderId) {
      window.fbq('track', 'Purchase', {
        content_type: 'product',
        content_ids: [orderId],
        value: parseFloat(orderTotal),
        currency: 'INR',
      });
      console.log('FB Pixel: Purchase event tracked', { orderId, total: orderTotal });
    }
    
    // Track purchase in our analytics (as backup/confirmation)
    if (orderId && orderTotal) {
      trackPurchase(orderId, parseFloat(orderTotal));
    }
    
    // Track Google Ads conversion (if configured)
    if (window.gtag && orderId) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL', // Will be replaced with actual values
        'value': parseFloat(orderTotal),
        'currency': 'INR',
        'transaction_id': orderId,
      });
    }
    
    // Trigger confetti animation
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Show content after a brief delay for effect
    setTimeout(() => setShowContent(true), 300);

    return () => clearInterval(interval);
  }, [orderId, orderTotal]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-b from-green-50 to-white">
      <Card className={`max-w-md w-full p-8 text-center transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        
        <h1 className="text-3xl font-bold text-green-700 mb-2">
          Order Placed!
        </h1>
        
        <p className="text-lg text-muted-foreground mb-6">
          {paymentMethod === 'cod' 
            ? 'Your order has been confirmed. Pay when it arrives!'
            : 'Payment successful! Your order is confirmed.'
          }
        </p>

        {orderId && (
          <div className="bg-muted p-4 rounded-lg mb-6">
            <p className="text-sm text-muted-foreground">Order ID</p>
            <p className="font-mono font-semibold">{orderId.slice(0, 8).toUpperCase()}</p>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Package className="w-4 h-4" />
            <span>Estimated delivery: 3-5 business days</span>
          </div>
          
          {paymentMethod === 'cod' && (
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
              Please keep ₹ ready for Cash on Delivery
            </p>
          )}
        </div>

        <div className="mt-8 space-y-3">
          <Button 
            onClick={() => {
              clearCart(); // Ensure cart is cleared
              navigate('/orders');
            }} 
            className="w-full rounded-xl"
            size="lg"
            data-testid="view-orders-btn"
          >
            View My Orders
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <Button 
            onClick={() => {
              clearCart(); // Ensure cart is cleared
              navigate('/');
            }} 
            variant="outline"
            className="w-full rounded-xl"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default OrderSuccessPage;
